"use client";

import { FiShare2 } from "react-icons/fi";
import { Box } from "@chakra-ui/react";
import { Button } from "@/components/ui";
import { useToast } from "@/components/feedback";
import { useThemeTokens } from "@/lib/theme/tokens";

interface ClassShareButtonProps {
  title: string;
  /** Light text on dark hero overlay */
  onDark?: boolean;
  /** Entity label for aria/copy. Default: "class" */
  subject?: string;
  /** Hide label on small screens; keeps aria-label for accessibility */
  iconOnlyOnMobile?: boolean;
}

export const ClassShareButton = ({
  title,
  onDark = false,
  subject = "class",
  iconOnlyOnMobile = true,
}: ClassShareButtonProps) => {
  const toast = useToast();
  const theme = useThemeTokens();

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied",
        description: `Share this ${subject} with family or friends.`,
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") return;
      try {
        await navigator.clipboard.writeText(url);
        toast({
          title: "Link copied",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } catch {
        toast({
          title: "Could not share",
          description: "Copy the URL from your browser address bar.",
          status: "error",
          duration: 4000,
          isClosable: true,
          position: "top-right",
        });
      }
    }
  };

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      leftIcon={<FiShare2 />}
      onClick={handleShare}
      aria-label={`Share this ${subject}`}
      color={onDark ? "white" : theme.text.primary}
      sx={{
        ...(onDark
          ? {
              bg: "whiteAlpha.200",
              "@media (hover: hover)": {
                "&:hover": { bg: "whiteAlpha.300" },
              },
            }
          : {}),
        ...(iconOnlyOnMobile
          ? {
              "@media (max-width: 47.99em)": {
                px: 2.5,
                minW: "auto",
                "& .chakra-button__icon": {
                  marginInlineEnd: 0,
                },
              },
            }
          : {}),
      }}
    >
      {iconOnlyOnMobile ? (
        <Box as="span" display={{ base: "none", md: "inline" }}>
          Share
        </Box>
      ) : (
        "Share"
      )}
    </Button>
  );
};
